import { useState, useRef, useEffect, useCallback } from "react";

/**
 * useDraggable — adds drag-to-move handlers to a fixed-position element.
 *
 * The position is held in transient component state (NOT persisted to
 * localStorage) so on every page refresh the element returns to its original
 * CSS-defined location.
 *
 * Returns the props to spread on the target element.
 */
export const useDraggable = () => {
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragRef = useRef({ active: false, moved: false, startX: 0, startY: 0, baseX: 0, baseY: 0 });
  const elRef = useRef(null);

  const onPointerDown = useCallback((e) => {
    dragRef.current = {
      active: true,
      moved: false,
      startX: e.clientX,
      startY: e.clientY,
      baseX: offset.x,
      baseY: offset.y,
    };
    // NOTE: do NOT capture the pointer here. Capturing on pointerdown makes the
    // browser fire the subsequent `click` on the capturing container instead of
    // the child button/Link, which silently breaks every tap on the floats.
    // We only capture once a real drag starts (see onPointerMove).
  }, [offset.x, offset.y]);

  const onPointerMove = useCallback((e) => {
    if (!dragRef.current.active) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    // Threshold: only start moving after 6px so a tap still registers as a click
    if (!dragRef.current.moved && Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
    if (!dragRef.current.moved) {
      dragRef.current.moved = true;
      try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* noop */ }
    }
    setOffset({ x: dragRef.current.baseX + dx, y: dragRef.current.baseY + dy });
  }, []);

  const onPointerUp = useCallback((e) => {
    const moved = dragRef.current.moved;
    dragRef.current.active = false;
    if (moved) {
      // Block the synthetic click that follows a drag so we don't trigger button onClick
      e.preventDefault();
      e.stopPropagation();
    }
    try { e.currentTarget.releasePointerCapture?.(e.pointerId); } catch { /* noop */ }
  }, []);

  // Snap back if the moved position is off-screen after a resize
  useEffect(() => {
    const handler = () => {
      if (!elRef.current) return;
      const r = elRef.current.getBoundingClientRect();
      if (r.right > window.innerWidth || r.left < 0 || r.bottom > window.innerHeight || r.top < 0) {
        setOffset({ x: 0, y: 0 });
      }
    };
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  return {
    ref: elRef,
    style: {
      transform: `translate3d(${offset.x}px, ${offset.y}px, 0)`,
      touchAction: "none",
    },
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel: onPointerUp,
    "data-draggable": "true",
  };
};
